import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useFilters, type PaymentFilter } from '../state/FilterProvider'
import { dealMatchesPayment, dealMatchesStatus } from '../utils/dealUrgency'
import type { Deal } from '../types/deal'

interface Props {
  allDeals: Deal[]
}

interface Option {
  key: PaymentFilter
  label: string
  dot: string
}

const GROUPS: Option[][] = [
  [{ key: 'all', label: 'All', dot: 'transparent' }],
  [
    { key: 'paid', label: 'Paid', dot: '#059669' },
    { key: 'partial', label: 'Partially Paid', dot: '#d97706' },
    { key: 'unpaid', label: 'Unpaid', dot: '#dc2626' },
    { key: 'scheduled', label: 'Scheduled', dot: '#3b82f6' },
  ],
]

const LABELS: Record<PaymentFilter, string> = {
  all: 'All',
  paid: 'Paid',
  partial: 'Partially Paid',
  unpaid: 'Unpaid',
  scheduled: 'Scheduled',
}

export const PaymentFilterDropdown: React.FunctionComponent<Props> = (props) => {
  const { allDeals } = props
  const { status, payment, setPayment } = useFilters()
  const [isOpen, setIsOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return undefined
    function handleClickOutside(event: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const counts = useMemo<Record<PaymentFilter, number>>(() => {
    const pool = allDeals.filter((deal) => dealMatchesStatus(deal, status))
    const next: Record<PaymentFilter, number> = {
      all: pool.length,
      paid: 0,
      partial: 0,
      unpaid: 0,
      scheduled: 0,
    }
    for (const filter of ['paid', 'partial', 'unpaid', 'scheduled'] as PaymentFilter[]) {
      next[filter] = pool.filter((deal) => dealMatchesPayment(deal, filter)).length
    }
    return next
  }, [allDeals, status])

  const currentLabel = LABELS[payment] ?? 'All'
  const currentCount = counts[payment] ?? 0

  return (
    <div
      className={`pay-dd-wrap${isOpen ? ' open' : ''}`}
      ref={wrapRef}
      style={{ position: 'relative', alignSelf: 'center' }}
    >
      <button
        type="button"
        className={`pay-dd-toggle${payment !== 'all' ? ' has-filter' : ''}`}
        onClick={() => setIsOpen((value) => !value)}
      >
        <span className="pay-dd-label">Payment:</span>
        <span className="pay-dd-value">{currentLabel}</span>
        <span className="pay-dd-count">{currentCount}</span>
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className="pay-dd-menu">
        {GROUPS.map((group, groupIndex) => (
          <div key={groupIndex}>
            {groupIndex > 0 && <div className="pay-dd-sep" />}
            {group.map((option) => (
              <div
                key={option.key}
                className={`pay-dd-item${payment === option.key ? ' active' : ''}`}
                onClick={() => {
                  setPayment(option.key)
                  setIsOpen(false)
                }}
              >
                <span
                  className="pay-dd-dot"
                  style={{
                    background: option.dot,
                    border: option.dot === 'transparent' ? '1px solid #cbd5e1' : undefined,
                  }}
                />
                <span className="pay-dd-text">{option.label}</span>
                <span className="chip-count">{counts[option.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
